/*
 * Đổi lượt khách trên server (/api/v2/reception/visits) sang Visit của App Tư vấn. Logic thuần.
 * Server giữ phần tiếp đón: khách, hàng chờ, trạng thái, người, phòng, giờ.
 * Các bước từ Phiếu tư vấn trở đi (báo giá, cọc, kết quả, lịch thực hiện, ghi chép) chưa có API nên giữ nguyên bản trên máy.
 */
import dayjs from 'dayjs';
import type { Mang } from '../Telesales/lead/logic';
import { maskPhone } from '../Telesales/lead/logic';
import { genderOf, isToday, type ApiSvc, type Source, type Visit, type VisitSt } from './logic';
import { mangOfApi, phoneKey } from './tele';

export interface ServerRef { id: number; name: string }
export type ServerQueue = 'consult' | 'exec';
export interface ServerVisit {
  id: number;
  code: string;
  visit_date?: string;
  has_declaration?: boolean;
  has_medical_alert?: boolean;
  branch?: ServerRef | null;
  /** gender: 0 chưa rõ, 1 Nam, 2 Nữ, 3 Khác; server lấy từ hồ sơ khách, hồ sơ chưa rõ thì lấy từ lịch hẹn */
  customer?: { id: number | null; code: string | null; name: string; phone: string | null; gender?: number | null } | null;
  appointment?: { id: number; code?: string | null; scheduled_at?: string | null; appointment_type?: string | null } | null;
  queue: ServerQueue;
  status: 'waiting' | 'consulting' | 'done' | 'cancelled';
  source?: string | null;
  mang?: string | null;
  need?: string | null;
  note?: string | null;
  visit_no?: number | null;
  check_in_at?: string | null;
  owner_staff?: ServerRef | null;
  page_staff?: ServerRef | null;
  consultant?: ServerRef | null;
  room?: ServerRef | null;
  assigned_at?: string | null;
  started_at?: string | null;
  tech?: ServerRef | null;
  doctor?: ServerRef | null;
  exec_room?: ServerRef | null;
  exec_at?: string | null;
  exec_done_at?: string | null;
  owner_tech?: ServerRef | null;
  owner_doctor?: ServerRef | null;
  notified_at?: string | null;
  wait_at?: string | null;
  done_at?: string | null;
  services?: { id: number; name: string; quantity?: number; price?: number }[];
}

const ts = (s?: string | null) => (s ? dayjs(String(s).replace(' ', 'T')).valueOf() : undefined);
const sid = (r?: ServerRef | null) => (r ? String(r.id) : undefined);

const SOURCES: Record<string, Source> = { appointment: 'Đặt lịch', walkin: 'Vãng lai', returning: 'Khách cũ', cskh: 'CSKH', telesales: 'Telesales' };
/** Server chỉ nhận walkin, cskh, returning khi tạo lượt tại quầy */
export const serverSource = (s: Source) => (s === 'Khách cũ' ? 'returning' : s === 'CSKH' ? 'cskh' : 'walkin');

/** Tên người trong lượt: để hiện tên cả khi người đó chưa có trong danh sách nhân sự đang tải */
export const namesOfVisit = (sv: ServerVisit): [string, string][] => [sv.consultant, sv.tech, sv.doctor, sv.owner_tech, sv.owner_doctor]
  .filter((r): r is ServerRef => !!r?.name).map((r) => [String(r.id), r.name]);

export function visitFromServer(sv: ServerVisit, prev: Visit | undefined, now: number): Visit {
  const checkInAt = ts(sv.check_in_at) ?? prev?.checkInAt ?? now;
  const svcs: ApiSvc[] = (sv.services ?? []).map((s) => ({ id: s.id, name: s.name, price: s.price ?? 0, qty: Math.max(1, s.quantity ?? 1) }));
  const need = sv.need || svcs.map((s) => s.name).join(', ') || prev?.need || 'Theo lịch hẹn';
  const phone = sv.customer?.phone;
  const hasOwner = sv.queue === 'exec' || !!sv.owner_tech || !!sv.owner_doctor;
  return {
    id: `sv-${sv.id}`,
    createdAt: checkInAt,
    deposits: [],
    plans: [],
    note: sv.note ?? '',
    log: [{ at: checkInAt, text: sv.appointment ? `Check-in lịch hẹn ${sv.appointment.code ?? `#${sv.appointment.id}`}` : 'Lễ tân tạo lượt tại quầy', by: 'Lễ tân' }],
    ...prev,
    seed: undefined,
    referral: undefined,
    serverId: sv.id,
    queue: sv.queue,
    hasDecl: !!sv.has_declaration,
    hasAlert: !!sv.has_medical_alert,
    code: sv.code,
    branchId: sv.branch ? String(sv.branch.id) : prev?.branchId ?? '',
    cust: {
      name: sv.customer?.name || prev?.cust.name || 'Khách',
      code: sv.customer?.code || prev?.cust.code || sv.appointment?.code || sv.code,
      phone: maskPhone(phone ?? undefined) || prev?.cust.phone || '—',
      // Server chưa rõ thì giữ xưng hô Lễ tân đã chọn trên máy (khách vãng lai không có hồ sơ)
      gender: genderOf(sv.customer?.gender) ?? prev?.cust.gender,
    },
    phoneDigits: phoneKey(phone) || prev?.phoneDigits,
    mang: (sv.mang as Mang) || prev?.mang || mangOfApi(need),
    need,
    source: prev?.source === 'Telesales' ? 'Telesales' : SOURCES[sv.source ?? ''] ?? prev?.source ?? 'Vãng lai',
    owner: sv.owner_staff?.name ?? prev?.owner,
    pageStaff: sv.page_staff?.name ?? prev?.pageStaff,
    apptAt: ts(sv.appointment?.scheduled_at) ?? prev?.apptAt,
    apiApptId: sv.appointment?.id ?? prev?.apiApptId,
    apptSvcs: prev?.apptSvcs?.length ? prev.apptSvcs : svcs.length ? svcs : prev?.apptSvcs,
    visitNo: sv.visit_no ?? prev?.visitNo ?? 1,
    checkInAt,
    status: sv.status as VisitSt,
    consultant: sid(sv.consultant),
    assignedAt: ts(sv.assigned_at),
    startedAt: ts(sv.started_at),
    room: sv.room?.name,
    exec: sv.exec_room && sv.tech
      ? { room: sv.exec_room.name, tech: String(sv.tech.id), doctor: sid(sv.doctor), at: ts(sv.exec_at) ?? now, by: prev?.exec?.by ?? 'Lễ tân', doneAt: ts(sv.exec_done_at) }
      : undefined,
    execOwner: hasOwner ? { tech: sid(sv.owner_tech), doctor: sid(sv.owner_doctor), notifiedAt: ts(sv.notified_at), waitAt: ts(sv.wait_at) } : undefined,
  };
}

/** Lượt trên máy ứng với lượt server: đã gắn id → cùng lịch hẹn → cùng số điện thoại trong ngày (lượt tạo trước khi nối API) */
const findLocal = (visits: Visit[], sv: ServerVisit, now: number) => {
  const byId = visits.findIndex((v) => v.serverId === sv.id);
  if (byId >= 0) return byId;
  const apptId = sv.appointment?.id;
  const byAppt = apptId ? visits.findIndex((v) => !v.serverId && v.apiApptId === apptId) : -1;
  if (byAppt >= 0) return byAppt;
  const key = phoneKey(sv.customer?.phone);
  return key ? visits.findIndex((v) => !v.serverId && !v.seed && v.phoneDigits === key && v.status !== 'referral' && isToday(v.checkInAt ?? v.createdAt, now)) : -1;
};

/** Gộp danh sách server vào store; không có gì đổi thì trả về chính mảng cũ để màn hình không vẽ lại */
export function mergeServerVisits(visits: Visit[], items: ServerVisit[], now: number): Visit[] {
  const out = [...visits];
  const added: Visit[] = [];
  let changed = false;
  items.forEach((sv) => {
    const i = findLocal(out, sv, now);
    const next = visitFromServer(sv, i >= 0 ? out[i] : undefined, now);
    if (i < 0) { added.push(next); changed = true; return; }
    if (JSON.stringify(next) !== JSON.stringify(out[i])) { out[i] = next; changed = true; }
  });
  return changed ? [...added, ...out] : visits;
}

/** Lịch "Làm dịch vụ" server xếp vào hàng thực hiện; Tái khám và các loại còn lại vào hàng tư vấn */
export const queueOfApptType = (t: unknown): ServerQueue => (t === 'Làm dịch vụ' ? 'exec' : 'consult');
